import { useState } from 'react';
import type { FormEvent } from 'react';
import type { EstimateFormErrors, EstimateFormValues, EstimateRequiredField } from '../../../types/estimate';
import { formatPhone, submitEstimateRequest } from '../../../utils/estimateSubmission';

type Status = 'idle' | 'submitting' | 'success' | 'error';

const initialValues: EstimateFormValues = {
  fullName: '',
  email: '',
  phone: '',
  address: '',
  propertyType: '',
  services: [],
  installAreas: '',
  details: '',
  preferPresent: false,
  honey: '',
};

const requiredOrder: EstimateRequiredField[] = ['fullName', 'email', 'phone', 'address', 'propertyType'];

export function useEstimateForm() {
  const [values, setValues] = useState<EstimateFormValues>(initialValues);
  const [touched, setTouched] = useState<Partial<Record<EstimateRequiredField, boolean>>>({});
  const [status, setStatus] = useState<Status>('idle');

  const allErrors = validate(values);
  const errors: EstimateFormErrors = {};
  requiredOrder.forEach((field) => {
    if (touched[field] && allErrors[field]) errors[field] = allErrors[field];
  });

  const setField = <K extends keyof EstimateFormValues>(field: K, value: EstimateFormValues[K]) => {
    setValues((v) => ({ ...v, [field]: field === 'phone' ? formatPhone(String(value)) : value }));
    if (status === 'error') setStatus('idle');
  };

  const toggleService = (service: string) => {
    setValues((v) => ({
      ...v,
      services: v.services.includes(service) ? v.services.filter((s) => s !== service) : [...v.services, service],
    }));
  };

  const touch = (field: EstimateRequiredField) => setTouched((t) => ({ ...t, [field]: true }));

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (status === 'submitting') return;
    setTouched(Object.fromEntries(requiredOrder.map((f) => [f, true])));
    const firstInvalid = requiredOrder.find((f) => allErrors[f]);
    if (firstInvalid) {
      document.getElementById(`estimate-${firstInvalid}`)?.focus();
      return;
    }
    setStatus('submitting');
    const result = await submitEstimateRequest(values);
    setStatus(result.ok ? 'success' : 'error');
  };

  const reset = () => {
    setValues(initialValues);
    setTouched({});
    setStatus('idle');
  };

  return { values, errors, status, setField, toggleService, touch, handleSubmit, reset };
}

function validate(v: EstimateFormValues): EstimateFormErrors {
  const e: EstimateFormErrors = {};
  if (v.fullName.trim().length < 2) e.fullName = 'Please enter your full name.';
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(v.email.trim())) e.email = 'Please enter a valid email address.';
  if (v.phone.replace(/\D/g, '').length !== 10) e.phone = 'Please enter a valid 10-digit phone number.';
  if (v.address.trim().length < 6) e.address = 'Please enter the property address.';
  if (!v.propertyType) e.propertyType = 'Please choose Residential or Commercial.';
  return e;
}
